import React, { useState, useRef, useEffect } from 'react';
import useDecks from '../hooks/useDecks';
import DeckSelector from '../components/DeckSelector';
import { hasUIChrome, stripUIChrome } from '../utils/text';

const AIAssistant = () => {
  const { decks: availableDecks } = useDecks();

  // ============================================
  // SOUS-ONGLET : 'chat' | 'text'
  // ============================================
  const [activeMode, setActiveMode] = useState('chat');

  // ============================================
  // ÉTATS ONGLET TEXTE (avec persistance localStorage)
  // ============================================
  const [textSourceLang, setTextSourceLang] = useState(() => {
    try { return localStorage.getItem('textDeck_sourceLang') || 'fr'; } catch { return 'fr'; }
  });
  const [textFrench, setTextFrench] = useState(() => {
    try { return localStorage.getItem('textDeck_french') || ''; } catch { return ''; }
  });
  const [textEnglish, setTextEnglish] = useState(() => {
    try { return localStorage.getItem('textDeck_english') || ''; } catch { return ''; }
  });
  const [textTigrinya, setTextTigrinya] = useState(() => {
    try { return localStorage.getItem('textDeck_tigrinya') || ''; } catch { return ''; }
  });
  const [textPronunciation, setTextPronunciation] = useState(() => {
    try { return localStorage.getItem('textDeck_pronunciation') || ''; } catch { return ''; }
  });
  const [textDeck, setTextDeck] = useState(() => {
    try { return localStorage.getItem('textDeck_deck') || ''; } catch { return ''; }
  });
  const [isTextTranslating, setIsTextTranslating] = useState(false);
  const [isTextSaving, setIsTextSaving] = useState(false);
  const [textSaved, setTextSaved] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedCards, setGeneratedCards] = useState([]);
  const [pendingCards, setPendingCards] = useState([]); // Cartes en attente d'édition/validation
  const [showPendingCardsModal, setShowPendingCardsModal] = useState(false); // Modal pour cartes détectées
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardBeingCreated, setCardBeingCreated] = useState(null);
  
  const messagesEndRef = useRef(null);

  // URL du backend API - Maintenant via N8N (disponible 24/7)
  const API_URL = process.env.REACT_APP_N8N_URL || 'https://n8n.automationtekleabsam.com/webhook';

  // ============================================
  // PERSISTANCE DE L'HISTORIQUE (localStorage)
  // ============================================
  useEffect(() => {
    // Charger l'historique au démarrage
    const savedMessages = localStorage.getItem('aiAssistantMessages');
    const savedPendingCards = localStorage.getItem('aiAssistantPendingCards');
    
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      } catch (e) {
        console.error('Erreur lors du chargement de l\'historique:', e);
      }
    }
    
    if (savedPendingCards) {
      try {
        const parsed = JSON.parse(savedPendingCards);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setPendingCards(parsed);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des cartes:', e);
      }
    }
  }, []); // Une seule fois au montage

  useEffect(() => {
    // Sauvegarder l'historique à chaque changement
    if (messages.length > 0) {
      localStorage.setItem('aiAssistantMessages', JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    // Sauvegarder les cartes en attente
    if (pendingCards.length > 0) {
      localStorage.setItem('aiAssistantPendingCards', JSON.stringify(pendingCards));
    } else {
      localStorage.removeItem('aiAssistantPendingCards');
    }
  }, [pendingCards]);

  // ============================================
  // PERSISTANCE ONGLET TEXTE (auto-save)
  // ============================================
  useEffect(() => { try { localStorage.setItem('textDeck_sourceLang', textSourceLang); } catch {} }, [textSourceLang]);
  useEffect(() => { try { localStorage.setItem('textDeck_french', textFrench); } catch {} }, [textFrench]);
  useEffect(() => { try { localStorage.setItem('textDeck_english', textEnglish); } catch {} }, [textEnglish]);
  useEffect(() => { try { localStorage.setItem('textDeck_tigrinya', textTigrinya); } catch {} }, [textTigrinya]);
  useEffect(() => { try { localStorage.setItem('textDeck_pronunciation', textPronunciation); } catch {} }, [textPronunciation]);
  useEffect(() => { if (textDeck) { try { localStorage.setItem('textDeck_deck', textDeck); } catch {} } }, [textDeck]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Appeler N8N qui gère Gemini (disponible 24/7)
      const response = await fetch(`${API_URL}/ai-assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: input,
          history: messages
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('📦 Réponse N8N complète:', data);
      console.log('📦 data.isFlashcard:', data.isFlashcard);
      console.log('📦 data.isMultipleFlashcards:', data.isMultipleFlashcards);
      console.log('📦 data.allFlashcards:', data.allFlashcards);
      console.log('📦 data.allFlashcards?.length:', data.allFlashcards?.length);
      console.log('📦 data.cardData:', data.cardData);
      console.log('📦 data.flashcardCount (N8N):', data.flashcardCount);
      
      // ============================================
      // GESTION DES FLASHCARDS (SIMPLE OU MULTIPLE)
      // ============================================
      let cardsToAdd = [];
      let detectedFlashcardCount = 0;
      
      if (data.success && data.isFlashcard) {
        if (data.isMultipleFlashcards && data.allFlashcards && data.allFlashcards.length > 1) {
          // 🎴 PLUSIEURS FLASHCARDS DÉTECTÉES
          cardsToAdd = data.allFlashcards;
          detectedFlashcardCount = data.allFlashcards.length;
          console.log(`🎴 ${detectedFlashcardCount} flashcards détectées (multiple) !`);
          
        } else if (data.cardData) {
          // 🎴 UNE SEULE FLASHCARD DÉTECTÉE
          cardsToAdd = [data.cardData];
          detectedFlashcardCount = 1;
          console.log('🎴 1 flashcard détectée (cardData)');
          
        } else if (data.allFlashcards && data.allFlashcards.length > 0) {
          // Support du cas où allFlashcards existe mais pas le flag multiple
          cardsToAdd = data.allFlashcards;
          detectedFlashcardCount = data.allFlashcards.length;
          console.log(`🎴 ${detectedFlashcardCount} flashcard(s) détectée(s) (fallback allFlashcards)`);
        }
      }
      
      console.log('✅ detectedFlashcardCount FINAL:', detectedFlashcardCount);
      console.log('✅ cardsToAdd.length:', cardsToAdd.length);
      
      const aiMessage = {
        role: 'assistant',
        content: data.response || data.reply || 'Réponse vide de l\'IA',
        cards: data.cards || [],
        flashcardCount: detectedFlashcardCount, // Calculé AVANT de créer le message
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      
      const currentMessageIndex = messages.length; // Index du message qu'on vient d'ajouter
      
      // Attacher l'index du message à chaque carte
      if (cardsToAdd.length > 0) {
        const cardsWithMessageIndex = cardsToAdd.map(card => ({
          ...card,
          messageIndex: currentMessageIndex
        }));
        
        // Ajouter aux cartes en attente
        setPendingCards(prev => [...prev, ...cardsWithMessageIndex]);
        
        console.log(`✅ ${cardsToAdd.length} carte(s) ajoutée(s) à pendingCards`);
        
        // NE PAS ouvrir automatiquement le modal
        // L'utilisateur cliquera sur le badge "✨ N cartes" dans le chat
      }

    } catch (error) {
      console.error('Erreur Backend:', error);
      const errorMessage = {
        role: 'assistant',
        content: `❌ Erreur : ${error.message}. Vérifiez que le backend est démarré sur ${API_URL}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveToNotion = async (card) => {
    try {
      console.log('🚀 Envoi de la carte:', card);
      
      // Adapter les champs selon le format (nouveau ou ancien)
      const cardData = {
        name: card.name || `${card.tigrinya || ''} - ${card.english || card.originalText || ''}`,
        originalText: card.originalText || card.english || '',
        frenchText: card.frenchText || card.french || card.textFrench || '',
        translationTigrinya: card.translationTigrinya || card.tigrinya || '',
        pronunciation: card.pronunciation || '',
        context: card.context || '',
        deck: card.deck || '📚 Vocabulaire Général'
      };
      
      // Sélection débordante recollée dans le champ tigrigna : on coupe avant
      // d'écrire dans Notion plutôt que d'y laisser passer l'interface.
      if (hasUIChrome(cardData.translationTigrinya)) {
        const cleaned = stripUIChrome(cardData.translationTigrinya);
        if (!cleaned) {
          alert('La traduction tigrigna ne contient que du texte de l\'application. Carte non enregistrée.');
          return;
        }
        if (!window.confirm(`La traduction tigrigna contient du texte de l'application.\n\nEnregistrer plutôt :\n${cleaned}`)) {
          return;
        }
        cardData.translationTigrinya = cleaned;
      }

      console.log('📦 Carte adaptée pour Notion:', cardData);

      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/create-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cardData)
      });

      console.log('📡 Réponse HTTP status:', response.status);
      
      // Si le statut est 200 ou 201, c'est un succès même sans JSON
      if (response.ok || response.status === 200 || response.status === 201) {
        try {
          const result = await response.json();
          console.log('✅ Résultat:', result);
          
          // Vérifier si la réponse contient success: true OU cardId (preuve de création)
          if (result.success || result.cardId) {
            alert(`✅ Carte "${card.name}" enregistrée dans Notion avec succès ! 🎉`);
            // Retirer la carte des cartes générées
            setGeneratedCards(prev => prev.filter(c => c !== card));
            setShowCardModal(false);
            setCardBeingCreated(null);
          } else {
            console.warn('⚠️ Réponse inattendue:', result);
            alert(`✅ Carte "${card.name}" enregistrée ! (Le serveur a répondu positivement)`);
            setShowCardModal(false);
            setCardBeingCreated(null);
          }
        } catch (jsonError) {
          // Si on ne peut pas parser le JSON mais que le status est OK, c'est quand même un succès
          console.log('✅ Succès sans JSON valide (HTTP 200)');
          alert(`✅ Carte "${card.name}" enregistrée dans Notion ! 🎉`);
          setGeneratedCards(prev => prev.filter(c => c !== card));
          setShowCardModal(false);
          setCardBeingCreated(null);
        }
      } else {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert(`❌ Erreur de connexion : ${error.message}\n\nVérifiez que le workflow n8n est activé.`);
    }
  };

  // ============================================
  // GESTION DES CARTES EN ATTENTE (ÉDITION)
  // ============================================
  
  const handleEditPendingCard = (index, field, value) => {
    setPendingCards(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Mettre à jour automatiquement le champ "name" (utilisé par Notion)
      if (field === 'tigrinya' || field === 'english') {
        const tigrinya = field === 'tigrinya' ? value : updated[index].tigrinya;
        const english = field === 'english' ? value : (updated[index].english || updated[index].originalText);
        updated[index].name = `${tigrinya} - ${english}`;
      }
      
      // S'assurer que le deck est défini
      if (!updated[index].deck) {
        updated[index].deck = '📚 Vocabulaire Général';
      }
      
      return updated;
    });
  };

  const handleDeletePendingCard = (index) => {
    setPendingCards(prev => prev.filter((_, i) => i !== index));
  };

  const handleSavePendingCard = async (index) => {
    const card = pendingCards[index];
    try {
      await handleSaveToNotion(card);
      // Retirer de la liste après enregistrement
      setPendingCards(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('❌ Erreur enregistrement:', error);
      alert(`❌ Erreur : ${error.message}`);
    }
  };

  const handleSaveAllPendingCards = async () => {
    let savedCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < pendingCards.length; i++) {
      try {
        await handleSaveToNotion(pendingCards[i]);
        savedCount++;
      } catch (error) {
        console.error('❌ Erreur:', error);
        errorCount++;
      }
    }
    
    setPendingCards([]);
    setShowPendingCardsModal(false);
    alert(
      `✅ ${savedCount} flashcards enregistrées !\n` +
      (errorCount > 0 ? `❌ ${errorCount} erreurs.` : '')
    );
  };

  // Fonction pour ouvrir le modal de création de carte à partir d'une réponse
  const handleCreateCardFromResponse = (messageContent) => {
    // Vérification que messageContent existe
    if (!messageContent || typeof messageContent !== 'string') {
      console.error('❌ messageContent invalide:', messageContent);
      alert('Erreur : Impossible d\'extraire la carte. Contenu du message invalide.');
      return;
    }

    let initialCard = {
      name: '',
      frenchText: '',
      translationTigrinya: '',
      pronunciation: '',
      context: '',
      deck: '📚 Vocabulaire Général'
    };

    // 1. Essayer d'extraire le JSON structuré
    const jsonMatch = messageContent.match(/```json\n([\s\S]*?)\n```/);
    
    if (jsonMatch) {
      try {
        const parsedData = JSON.parse(jsonMatch[1]);
        initialCard = {
          name: parsedData.en || parsedData.name || '',
          frenchText: parsedData.fr || parsedData.frenchText || '',
          translationTigrinya: parsedData.ti || parsedData.translationTigrinya || '',
          pronunciation: parsedData.translit || parsedData.pronunciation || '',
          context: parsedData.context || '',
          deck: parsedData.deck || '📚 Vocabulaire Général'
        };
      } catch (e) {
        console.warn('Erreur parsing JSON:', e);
      }
    }

    // 2. Si pas de JSON ou champs vides, extraction intelligente du texte
    if (!initialCard.name || !initialCard.translationTigrinya) {
      // Détecter les patterns communs dans les réponses de Gemini
      
      // Pattern 1: "en": "word" ou **en**: word
      const enPattern = /"en":\s*"([^"]+)"|"english":\s*"([^"]+)"|\*\*en\*\*:\s*([^\n]+)/i;
      const enMatch = messageContent.match(enPattern);
      if (enMatch && !initialCard.name) {
        initialCard.name = (enMatch[1] || enMatch[2] || enMatch[3] || '').trim();
      }

      // Pattern 2: "fr": "mot" ou **fr**: mot
      const frPattern = /"fr":\s*"([^"]+)"|"french":\s*"([^"]+)"|\*\*fr\*\*:\s*([^\n]+)/i;
      const frMatch = messageContent.match(frPattern);
      if (frMatch && !initialCard.frenchText) {
        initialCard.frenchText = (frMatch[1] || frMatch[2] || frMatch[3] || '').trim();
      }

      // Pattern 3: "ti": "ትግርኛ" ou **ti**: ትግርኛ (alphabet guèze)
      const tiPattern = /"ti":\s*"([^"]+)"|"tigrinya":\s*"([^"]+)"|\*\*ti\*\*:\s*([^\n]+)|🇪🇷.*?([ሀ-፼]+)/;
      const tiMatch = messageContent.match(tiPattern);
      if (tiMatch && !initialCard.translationTigrinya) {
        initialCard.translationTigrinya = (tiMatch[1] || tiMatch[2] || tiMatch[3] || tiMatch[4] || '').trim();
      }

      // Pattern 4: Prononciation "translit": "..." ou entre parenthèses après le tigrinya
      const pronPattern = /"translit":\s*"([^"]+)"|"pronunciation":\s*"([^"]+)"|\*\*translit\*\*:\s*([^\n]+)|\(([a-zA-Z\s]+)\)/;
      const pronMatch = messageContent.match(pronPattern);
      if (pronMatch && !initialCard.pronunciation) {
        initialCard.pronunciation = (pronMatch[1] || pronMatch[2] || pronMatch[3] || pronMatch[4] || '').trim();
      }

      // Pattern 5: Contexte après "context" ou "Context"
      const contextPattern = /"context":\s*"([^"]+)"|Context[:\s]+([^\n]+)/i;
      const contextMatch = messageContent.match(contextPattern);
      if (contextMatch && !initialCard.context) {
        initialCard.context = (contextMatch[1] || contextMatch[2] || '').trim();
      }

      // Pattern 6: Si toujours rien, chercher les caractères guèze (tigrinya) directement
      if (!initialCard.translationTigrinya) {
        const guezPattern = /([ሀ-፼]{2,})/;
        const guezMatch = messageContent.match(guezPattern);
        if (guezMatch) {
          initialCard.translationTigrinya = guezMatch[1].trim();
        }
      }

      // Pattern 7: Détecter les mots/phrases clés pour choisir le bon deck
      const deckKeywords = {
        '🗣️ Conversation de base': ['bonjour', 'hello', 'merci', 'thank', 'comment vas', 'how are'],
        '🏥 Médical': ['médecin', 'doctor', 'hôpital', 'hospital', 'malade', 'sick', 'médicament', 'medicine'],
        '✈️ Voyage': ['aéroport', 'airport', 'voyage', 'travel', 'hôtel', 'hotel', 'taxi'],
        '🍽️ Nourriture': ['manger', 'eat', 'nourriture', 'food', 'restaurant', 'boire', 'drink'],
        '📖 Verbes': ['conjugue', 'verbe', 'conjugate', 'verb', 'infinitive'],
        '🔢 Nombres': ['nombre', 'number', 'chiffre', 'compter', 'count', /\b\d+\b/],
      };

      const contentLower = messageContent.toLowerCase();
      for (const [deck, keywords] of Object.entries(deckKeywords)) {
        for (const keyword of keywords) {
          if (typeof keyword === 'string' && contentLower.includes(keyword)) {
            initialCard.deck = deck;
            break;
          } else if (keyword instanceof RegExp && keyword.test(contentLower)) {
            initialCard.deck = deck;
            break;
          }
        }
        if (initialCard.deck !== '📚 Vocabulaire Général') break;
      }
    }

    // 3. Normalisation : retirer les guillemets, espaces superflus, etc.
    Object.keys(initialCard).forEach(key => {
      if (typeof initialCard[key] === 'string') {
        initialCard[key] = initialCard[key]
          .replace(/^["'\s]+|["'\s]+$/g, '') // Retirer guillemets et espaces aux extrémités
          .replace(/\*\*/g, '') // Retirer le markdown bold
          .trim();
      } else if (typeof initialCard[key] === 'object' && initialCard[key] !== null) {
        // Si c'est un objet (ex: conjugaison {m: "...", f: "..."}), le convertir en string
        initialCard[key] = JSON.stringify(initialCard[key]);
      }
    });

    setCardBeingCreated(initialCard);
    setShowCardModal(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedPrompts = [
    "Traduis 'Bonjour, comment vas-tu ?' en tigrinya",
    "Apprends-moi le verbe 'manger' en tigrinya avec ses conjugaisons",
    "Vocabulaire médical : comment dire 'hôpital', 'médecin', 'médicament' ?",
    "Expressions de politesse en tigrinya",
    "Les nombres de 1 à 10 en tigrinya"
  ];

  // ============================================
  // FONCTIONS ONGLET TEXTE
  // ============================================

  // Texte source selon la langue active
  const getTextSource = () => {
    if (textSourceLang === 'fr') return textFrench;
    if (textSourceLang === 'en') return textEnglish;
    return textTigrinya;
  };

  const handleTextTranslate = async () => {
    const source = getTextSource();
    if (!source.trim()) return;
    setIsTextTranslating(true);

    let prompt = '';
    if (textSourceLang === 'fr') {
      prompt = `Traduis ce texte du français vers l'anglais ET le tigrinya. Réponds UNIQUEMENT avec ce JSON valide (sans markdown) :
{"english":"traduction anglaise","tigrinya":"traduction en alphabet guèze","pronunciation":"translittération phonétique latine du tigrinya"}

Texte français : ${source}`;
    } else if (textSourceLang === 'en') {
      prompt = `Traduis ce texte de l'anglais vers le français ET le tigrinya. Réponds UNIQUEMENT avec ce JSON valide (sans markdown) :
{"french":"traduction française","tigrinya":"traduction en alphabet guèze","pronunciation":"translittération phonétique latine du tigrinya"}

Texte anglais : ${source}`;
    } else {
      prompt = `Traduis ce texte tigrinya vers le français ET l'anglais. Réponds UNIQUEMENT avec ce JSON valide (sans markdown) :
{"french":"traduction française","english":"traduction anglaise","pronunciation":"translittération phonétique latine du tigrinya"}

Texte tigrinya : ${source}`;
    }

    try {
      const response = await fetch(`${API_URL}/ai-assistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: prompt, history: [] })
      });
      const data = await response.json();
      const reply = data.response || data.reply || '';
      let parsed = null;
      try { parsed = JSON.parse(reply); } catch { /* continue */ }
      if (!parsed) {
        const match = reply.match(/\{[\s\S]*?\}/);
        if (match) try { parsed = JSON.parse(match[0]); } catch { /* continue */ }
      }
      if (parsed) {
        if (parsed.english) setTextEnglish(parsed.english);
        if (parsed.french) setTextFrench(parsed.french);
        if (parsed.tigrinya) setTextTigrinya(parsed.tigrinya);
        if (parsed.pronunciation) setTextPronunciation(parsed.pronunciation);
      }
    } catch (error) {
      alert(`Erreur traduction : ${error.message}`);
    } finally {
      setIsTextTranslating(false);
    }
  };

  const handleTextSave = async () => {
    if (!textTigrinya.trim() || (!textEnglish.trim() && !textFrench.trim())) return;
    setIsTextSaving(true);
    try {
      const deck = textDeck || availableDecks[0] || '📚 Vocabulaire Général';
      const payload = {
        name: textEnglish || textFrench,
        originalText: textEnglish,
        frenchText: textFrench,
        translationTigrinya: textTigrinya,
        pronunciation: textPronunciation,
        context: '',
        deck
      };
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/create-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setTextSaved(true);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      alert(`Erreur enregistrement : ${error.message}`);
    } finally {
      setIsTextSaving(false);
    }
  };

  const handleTextReset = () => {
    setTextFrench('');
    setTextEnglish('');
    setTextTigrinya('');
    setTextPronunciation('');
    setTextSaved(false);
    try {
      localStorage.removeItem('textDeck_french');
      localStorage.removeItem('textDeck_english');
      localStorage.removeItem('textDeck_tigrinya');
      localStorage.removeItem('textDeck_pronunciation');
    } catch {}
  };

  // Initialiser le deck texte quand les decks chargent
  useEffect(() => {
    if (availableDecks.length > 0 && !textDeck) {
      setTextDeck(availableDecks[0]);
    }
  }, [availableDecks, textDeck]);

  return (
    <div className="min-h-screen bg-sand-50 pb-20 flex flex-col">
      {/* Header */}
      <header className="bg-sand-50/95 backdrop-blur border-b border-sand-200 sticky top-0 z-40">
        <div className="max-w-screen-xl mx-auto px-4 pt-8 pb-0">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="font-display text-[2rem] leading-none text-ink-900">Assistant</h1>
              <p className="label-caps mt-2">Propulsé par Gemini</p>
            </div>

            {/* Bouton Effacer Historique (visible uniquement en mode chat) */}
            {activeMode === 'chat' && messages.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('Voulez-vous vraiment effacer tout l\'historique des conversations ?')) {
                    setMessages([]);
                    setPendingCards([]);
                    localStorage.removeItem('aiAssistantMessages');
                    localStorage.removeItem('aiAssistantPendingCards');
                  }
                }}
                className="shrink-0 label-caps hover:text-clay-600 transition-colors"
              >
                Effacer l'historique
              </button>
            )}
            {/* Bouton reset texte (visible uniquement en mode texte) */}
            {activeMode === 'text' && (textFrench || textEnglish || textTigrinya) && (
              <button
                onClick={handleTextReset}
                className="shrink-0 label-caps hover:text-clay-600 transition-colors"
              >
                Effacer
              </button>
            )}
          </div>

          {/* Sous-onglets */}
          <div className="flex gap-6">
            {[['chat', 'Chat'], ['text', 'Texte']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setActiveMode(mode)}
                className={`relative pb-3 text-sm font-medium transition-colors ${
                  activeMode === mode ? 'text-ink-900' : 'text-ink-400 hover:text-ink-700'
                }`}
              >
                {label}
                <span
                  className={`absolute -bottom-px left-0 right-0 h-[2px] rounded-full transition-opacity ${
                    activeMode === mode ? 'bg-clay-500 opacity-100' : 'opacity-0'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ============================================ */}
      {/* ONGLET TEXTE                               */}
      {/* ============================================ */}
      {activeMode === 'text' && (
        <div className="flex-1 max-w-screen-xl mx-auto w-full px-4 py-5 space-y-4">
          {textSaved ? (
            /* Confirmation */
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="font-display text-3xl text-ink-900 mb-2">Carte enregistrée</h2>
              <p className="text-ink-500 mb-1">Ajoutée dans le deck</p>
              <p className="font-semibold text-clay-700 mb-6">{textDeck}</p>
              <button
                onClick={handleTextReset}
                className="px-6 py-3 bg-clay-500 text-onAccent rounded-xl font-semibold hover:bg-clay-600 transition-colors"
              >
                Ajouter un autre texte
              </button>
            </div>
          ) : (
            <>
              {/* Deck */}
              <DeckSelector
                value={textDeck}
                onChange={setTextDeck}
                decks={availableDecks}
                label="Deck"
              />

              {/* Sélecteur de langue source */}
              <div className="flex items-center gap-2">
                <span className="label-caps">Je saisis en</span>
                <div className="flex bg-sand-100 rounded-lg p-0.5 gap-0.5">
                  {[
                    { lang: 'fr', flag: '', label: 'Français' },
                    { lang: 'en', flag: '', label: 'English' },
                    { lang: 'ti', flag: '', label: 'Tigrinya' },
                  ].map(({ lang, flag, label }) => (
                    <button
                      key={lang}
                      onClick={() => setTextSourceLang(lang)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                        textSourceLang === lang ? 'bg-surface text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-700'
                      }`}
                    >
                      {flag} {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Champ Français */}
              <div className={`bg-surface rounded-xl shadow-card border-2 overflow-hidden transition-all ${
                textSourceLang === 'fr' ? 'border-clay-500' : 'border-sand-200'
              }`}>
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                  
                  <span className="text-sm font-bold text-ink-700">Français</span>
                  {textSourceLang === 'fr' && <span className="text-xs text-clay-500 font-medium">(langue source)</span>}
                </div>
                <textarea
                  value={textFrench}
                  onChange={(e) => { setTextFrench(e.target.value); setTextSaved(false); }}
                  placeholder="Texte en français..."
                  className="bg-surface w-full px-4 pb-3 text-ink-900 focus:outline-none resize-none text-base leading-relaxed"
                  rows="3"
                  disabled={isTextTranslating}
                />
              </div>

              {/* Champ Anglais */}
              <div className={`bg-surface rounded-xl shadow-card border-2 overflow-hidden transition-all ${
                textSourceLang === 'en' ? 'border-clay-500' : 'border-sand-200'
              }`}>
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                  
                  <span className="text-sm font-bold text-ink-700">English</span>
                  {textSourceLang === 'en' && <span className="text-xs text-clay-500 font-medium">(source language)</span>}
                </div>
                <textarea
                  value={textEnglish}
                  onChange={(e) => { setTextEnglish(e.target.value); setTextSaved(false); }}
                  placeholder="Text in English..."
                  className="bg-surface w-full px-4 pb-3 text-ink-900 focus:outline-none resize-none text-base leading-relaxed"
                  rows="3"
                  disabled={isTextTranslating}
                />
              </div>

              {/* Champ Tigrinya */}
              <div className={`bg-surface rounded-xl shadow-card border-2 overflow-hidden transition-all ${
                textSourceLang === 'ti' ? 'border-clay-500' : 'border-sand-200'
              }`}>
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                  
                  <span className="text-sm font-medium text-ink-900">Tigrigna</span>
                  {textSourceLang === 'ti' && <span className="text-xs text-clay-500 font-medium">(langue source)</span>}
                </div>
                <textarea
                  value={textTigrinya}
                  onChange={(e) => { setTextTigrinya(e.target.value); setTextSaved(false); }}
                  placeholder="ትርጉም ብትግርኛ..."
                  className="bg-surface w-full px-4 pb-3 text-ink-900 font-geez focus:outline-none resize-none text-lg leading-relaxed"
                  rows="3"
                  disabled={isTextTranslating}
                />
              </div>

              {/* Prononciation */}
              <div className="bg-surface rounded-xl shadow-card border border-sand-200 px-4 py-3">
                <label className="block text-sm font-semibold text-ink-700 mb-1.5">🔊 Prononciation (optionnel)</label>
                <input
                  type="text"
                  value={textPronunciation}
                  onChange={(e) => setTextPronunciation(e.target.value)}
                  placeholder="Translittération phonétique du tigrinya..."
                  className="w-full px-3 py-2 border border-sand-200 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none text-sm text-ink-500 italic bg-sand-50"
                />
              </div>

              {/* Bouton Gemini */}
              <button
                onClick={handleTextTranslate}
                disabled={isTextTranslating || !getTextSource().trim()}
                className="w-full py-3 bg-clay-500 text-onAccent rounded-xl font-semibold hover:bg-clay-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-card"
              >
                {isTextTranslating ? (
                  <>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-surface rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-surface rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <div className="w-2 h-2 bg-surface rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </div>
                    <span>Gemini traduit...</span>
                  </>
                ) : (
                  <>
                    <span>🤖</span>
                    <span>
                      {textSourceLang === 'fr' && 'Traduire → Anglais + Tigrinya'}
                      {textSourceLang === 'en' && 'Translate → Français + Tigrinya'}
                      {textSourceLang === 'ti' && 'ተርጉም → Français + English'}
                    </span>
                  </>
                )}
              </button>

              {/* Aperçu */}
              {(textFrench || textEnglish || textTigrinya) && (
                <div className="bg-sand-100 rounded-xl border border-sand-200 p-4">
                  <p className="label-caps mb-3">Aperçu de la carte</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-surface rounded-lg p-3 shadow-card space-y-1.5">
                      <p className="text-xs text-ink-400 font-bold uppercase">RECTO</p>
                      {textEnglish && <p className="text-xs text-ink-700">{textEnglish}</p>}
                      {textFrench && <p className="text-xs text-ink-700">{textFrench}</p>}
                      {!textEnglish && !textFrench && <p className="text-xs text-ink-400">—</p>}
                    </div>
                    <div className="bg-surface rounded-lg p-3 shadow-card">
                      <p className="text-xs text-ink-400 font-bold uppercase mb-1.5">VERSO</p>
                      <p className="font-geez text-base text-ink-900">{textTigrinya || '—'}</p>
                      {textPronunciation && <p className="text-xs text-ink-500 italic mt-1">{textPronunciation}</p>}
                    </div>
                  </div>
                  <p className="text-xs text-clay-600 font-medium mt-2">📚 {textDeck}</p>
                </div>
              )}

              {/* Enregistrer */}
              <button
                onClick={handleTextSave}
                disabled={isTextSaving || !textTigrinya.trim() || (!textEnglish.trim() && !textFrench.trim())}
                className="w-full py-4 bg-clay-500 text-onAccent rounded-xl font-bold text-base hover:bg-clay-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lift flex items-center justify-center gap-2"
              >
                {isTextSaving ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <><span>💾</span><span>Enregistrer dans le deck</span></>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Container principal : Chat + Panneau latéral */}
      {activeMode === 'chat' && <div className="flex-1 flex flex-col lg:flex-row relative overflow-hidden">
        <div className={`flex-1 transition-all duration-300 ${
          showPendingCardsModal ? 'lg:mr-[400px]' : ''
        } ${showPendingCardsModal ? 'h-1/2 lg:h-auto' : 'h-auto'}`}>
          <div className="max-w-4xl mx-auto px-4 py-6 h-full overflow-y-auto">
        
        {/* Messages d'accueil si chat vide */}
        {messages.length === 0 && (
          <div className="mb-6">
            <div className="bg-surface rounded-xl p-6 shadow-card border border-sand-200 mb-4">
              <h2 className="font-display text-2xl text-ink-900 mb-3">
                Bienvenue
              </h2>
              <p className="text-ink-500 mb-4">
                Je peux vous aider à créer des cartes de révision en tigrinya avec :
              </p>
              <ul className="space-y-2 text-ink-700">
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-clay-400 shrink-0" />
                  <span>Traductions précises anglais/français → tigrinya</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-clay-400 shrink-0" />
                  <span>Prononciation phonétique</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-clay-400 shrink-0" />
                  <span>Contexte et exemples d'utilisation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-clay-400 shrink-0" />
                  <span>Conjugaisons pour les verbes</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-clay-400 shrink-0" />
                  <span>Sauvegarde directe dans Notion</span>
                </li>
              </ul>
            </div>

            {/* Suggestions de prompts */}
            <div className="bg-surface rounded-xl p-5 border border-sand-200">
              <h3 className="label-caps mb-4">Essayez par exemple</h3>
              <div className="space-y-2">
                {suggestedPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(prompt)}
                    className="w-full text-left px-4 py-2 bg-surface rounded-lg text-sm text-ink-700 hover:border-clay-400 hover:text-clay-700 transition-colors border border-sand-200"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Zone de messages */}
        <div className="space-y-4 mb-6">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-clay-500 text-onAccent'
                    : 'bg-surface text-ink-900 shadow-card border border-sand-200'
                }`}
              >
                <div className="whitespace-pre-wrap">{msg.content || 'Message vide'}</div>
                
                {/* Badge "✨ N cartes détectées" DANS LE CHAT */}
                {msg.role === 'assistant' && msg.flashcardCount > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => {
                        console.log('📦 Clic sur badge, pendingCards:', pendingCards);
                        console.log('📦 flashcardCount:', msg.flashcardCount);
                        
                        // Ouvrir le modal directement (toutes les cartes en attente)
                        setShowPendingCardsModal(true);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-clay-500 text-onAccent rounded-full font-semibold hover:bg-clay-600 transition-all shadow-card text-sm"
                    >
                      <span>✨</span>
                      <span>{msg.flashcardCount} carte{msg.flashcardCount > 1 ? 's' : ''} détectée{msg.flashcardCount > 1 ? 's' : ''}</span>
                      <span>→</span>
                    </button>
                    <p className="text-xs text-ink-500 mt-2">
                      Cliquez pour éditer et enregistrer dans Notion
                    </p>
                  </div>
                )}
                
                {/* Bouton pour créer une carte à partir de cette réponse */}
                {msg.role === 'assistant' && msg.content && !msg.content.includes('❌ Erreur') && !msg.flashcardCount && (
                  <button
                    onClick={() => handleCreateCardFromResponse(msg.content)}
                    className="mt-3 w-full py-2 bg-clay-500 text-onAccent rounded-lg font-medium hover:bg-clay-600 transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <span>➕</span>
                    <span>Créer une carte depuis cette réponse</span>
                  </button>
                )}
                
                {/* Afficher les cartes générées (ancien format, pour compatibilité) */}
                {msg.cards && msg.cards.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <div className="text-sm font-semibold opacity-75">
                      📝 {msg.cards.length} carte(s) générée(s) :
                    </div>
                    {msg.cards.map((card, cardIdx) => (
                      <div
                        key={cardIdx}
                        className="bg-sand-100 rounded-lg p-4 border border-sand-200"
                      >
                        <div className="space-y-2 text-sm">
                          <div><strong>Recto</strong> {card.name} {card.frenchText && `/ ${card.frenchText}`}</div>
                          <div className="font-geez"><strong className="font-sans">Verso</strong> {card.translationTigrinya}</div>
                          {card.pronunciation && <div><strong>🔊</strong> {card.pronunciation}</div>}
                          {card.context && <div><strong>💬</strong> {card.context}</div>}
                          {card.deck && <div><strong>📚</strong> {card.deck}</div>}
                        </div>
                        <button
                          onClick={() => handleSaveToNotion(card)}
                          className="mt-3 w-full py-2 bg-clay-500 text-onAccent rounded-lg font-medium hover:bg-clay-600 transition-colors text-sm"
                        >
                          💾 Enregistrer dans Notion
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="text-xs opacity-50 mt-2">
                  {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-surface rounded-xl px-4 py-3 shadow-card border border-sand-200">
                <div className="flex items-center gap-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-clay-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-clay-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-clay-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-ink-500">Gemini réfléchit...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Cartes en attente de sauvegarde */}
        {generatedCards.length > 0 && (
          <div className="mb-6 bg-surface rounded-xl p-5 border border-sand-200">
            <h3 className="font-semibold text-ink-800 mb-3">
              ⚠️ {generatedCards.length} carte(s) en attente de sauvegarde
            </h3>
            <div className="space-y-2">
              {generatedCards.map((card, i) => (
                <div key={i} className="flex items-center justify-between bg-surface rounded-lg p-3">
                  <div className="flex-1">
                    <div className="font-medium">{card.name} → {card.translationTigrinya}</div>
                    <div className="text-sm text-ink-500">{card.deck}</div>
                  </div>
                  <button
                    onClick={() => handleSaveToNotion(card)}
                    className="ml-3 px-4 py-2 bg-clay-500 text-onAccent rounded-lg text-sm hover:bg-clay-600"
                  >
                    💾 Sauvegarder
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Zone de saisie */}
        <div className="bg-surface rounded-xl shadow-lift border border-sand-200 sticky bottom-20 z-30">
          <div className="p-4">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question (ex: 'Traduis bonjour en tigrinya')..."
              className="bg-surface w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-clay-500/20 resize-none"
              rows="3"
              disabled={isLoading}
            />
            <div className="flex items-center justify-between mt-3">
              <div className="text-xs text-ink-500">
                Entrée pour envoyer
              </div>
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !input.trim()}
                className="px-6 py-2 bg-clay-500 text-onAccent rounded-lg font-medium hover:bg-clay-600 transition-colors disabled:bg-sand-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span>Envoyer</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>}

      {/* ============================================ */}
      {/* MODAL CENTRÉ - ÉDITION DES FLASHCARDS      */}
      {/* ============================================ */}
      {showPendingCardsModal && pendingCards.length > 0 && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl shadow-lift w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header du modal */}
            <div className="bg-sand-50 border-b border-sand-200 px-6 py-5 flex items-center justify-between">
              <h2 className="font-display text-2xl">
                ✨ {pendingCards.length} Flashcard{pendingCards.length > 1 ? 's' : ''}
              </h2>
              <button 
                onClick={() => setShowPendingCardsModal(false)}
                className="text-ink-400 hover:text-ink-900 text-3xl leading-none">
                ×
              </button>
            </div>

            {/* Actions globales */}
            <div className="p-4 bg-sand-50 border-b flex justify-between items-center">
              <span className="text-sm text-ink-500">
                Éditez les flashcards avant de les enregistrer
              </span>
              <div className="flex gap-2">
                <button 
                  onClick={handleSaveAllPendingCards}
                  className="px-6 py-2 bg-clay-500 text-onAccent rounded-lg font-semibold hover:bg-clay-600 transition-all shadow-card">
                  💾 Enregistrer toutes
                </button>
                <button
                  onClick={() => {
                    setPendingCards([]);
                    setShowPendingCardsModal(false);
                  }}
                  className="px-4 py-2 bg-sand-200 text-ink-700 rounded-lg font-semibold hover:bg-sand-300 transition-all">
                  Annuler
                </button>
              </div>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {pendingCards.map((card, index) => (
                  <div key={index} className="bg-surface rounded-lg p-5 border border-sand-200 shadow-card">
                    {/* Header de la carte */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-semibold text-clay-600">
                        Flashcard #{index + 1}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSavePendingCard(index)}
                          className="px-4 py-1.5 bg-rating-easy text-onAccent rounded text-sm hover:bg-green-700 transition-colors">
                          ✅ Enregistrer
                        </button>
                        <button
                          onClick={() => handleDeletePendingCard(index)}
                          className="px-4 py-1.5 bg-rating-forgot text-onAccent rounded text-sm hover:bg-rating-forgot transition-colors">
                          🗑️ Supprimer
                        </button>
                      </div>
                    </div>

                    {/* Champs éditables */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Tigrinya */}
                      <div>
                        <label className="block text-xs font-semibold text-ink-700 mb-1">
                          Tigrigna
                        </label>
                        <input
                          type="text"
                          value={card.tigrinya || ''}
                          onChange={(e) => handleEditPendingCard(index, 'tigrinya', e.target.value)}
                          className="bg-surface w-full px-3 py-2 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none text-lg"
                          placeholder="ትግርኛ"
                        />
                      </div>

                      {/* English */}
                      <div>
                        <label className="block text-xs font-semibold text-ink-700 mb-1">
                          Anglais
                        </label>
                        <input
                          type="text"
                          value={card.english || card.originalText || ''}
                          onChange={(e) => {
                            handleEditPendingCard(index, 'english', e.target.value);
                            handleEditPendingCard(index, 'originalText', e.target.value);
                          }}
                          className="bg-surface w-full px-3 py-2 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none"
                          placeholder="English translation"
                        />
                      </div>

                      {/* French */}
                      <div>
                        <label className="block text-xs font-semibold text-ink-700 mb-1">
                          Français
                        </label>
                        <input
                          type="text"
                          value={card.french || card.textFrench || ''}
                          onChange={(e) => {
                            handleEditPendingCard(index, 'french', e.target.value);
                            handleEditPendingCard(index, 'textFrench', e.target.value);
                          }}
                          className="bg-surface w-full px-3 py-2 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none"
                          placeholder="Traduction française"
                        />
                      </div>

                      {/* Pronunciation */}
                      <div>
                        <label className="block text-xs font-semibold text-ink-700 mb-1">
                          🔊 Prononciation
                        </label>
                        <input
                          type="text"
                          value={card.pronunciation || ''}
                          onChange={(e) => handleEditPendingCard(index, 'pronunciation', e.target.value)}
                          className="bg-surface w-full px-3 py-2 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none"
                          placeholder="Phonétique"
                        />
                      </div>

                      {/* Deck */}
                      <div className="md:col-span-2">
                        <DeckSelector
                          value={card.deck || '📚 Vocabulaire Général'}
                          onChange={(v) => handleEditPendingCard(index, 'deck', v)}
                          decks={availableDecks}
                          label="Deck"
                          className="text-sm py-2"
                        />
                      </div>

                      {/* Context */}
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-ink-700 mb-1">
                          💬 Contexte / Exemple
                        </label>
                        <textarea
                          value={card.context || ''}
                          onChange={(e) => handleEditPendingCard(index, 'context', e.target.value)}
                          className="bg-surface w-full px-3 py-2 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none resize-none"
                          rows="2"
                          placeholder="Exemple d'utilisation"
                        />
                      </div>

                      {/* Notes */}
                      {card.notes && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold text-ink-700 mb-1">
                            📝 Notes
                          </label>
                          <div className="text-sm text-ink-500 bg-sand-50 rounded p-2">
                            {card.notes}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création/édition de carte */}
      {showCardModal && cardBeingCreated && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-sand-50 border-b border-sand-200 px-6 py-5 rounded-t-xl">
              <h2 className="font-display text-2xl">Créer une carte</h2>
              <p className="label-caps mt-2">Enregistrée directement dans Notion</p>
            </div>

            <div className="p-6 space-y-4">
              {/* Champ Anglais */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  Anglais — recto
                </label>
                <input
                  type="text"
                  value={cardBeingCreated.name}
                  onChange={(e) => setCardBeingCreated({...cardBeingCreated, name: e.target.value})}
                  placeholder="Ex: Hello"
                  className="bg-surface w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none"
                />
              </div>

              {/* Champ Français */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  Français — recto (optionnel)
                </label>
                <input
                  type="text"
                  value={cardBeingCreated.frenchText}
                  onChange={(e) => setCardBeingCreated({...cardBeingCreated, frenchText: e.target.value})}
                  placeholder="Ex: Bonjour"
                  className="bg-surface w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none"
                />
              </div>

              {/* Champ Tigrinya */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  Tigrigna — verso
                </label>
                <input
                  type="text"
                  value={cardBeingCreated.translationTigrinya}
                  onChange={(e) => setCardBeingCreated({...cardBeingCreated, translationTigrinya: e.target.value})}
                  placeholder="Ex: ሰላም"
                  className="bg-surface w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none text-lg"
                  dir="rtl"
                />
              </div>

              {/* Champ Prononciation */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  🔊 Prononciation (BACK de la carte)
                </label>
                <input
                  type="text"
                  value={cardBeingCreated.pronunciation}
                  onChange={(e) => setCardBeingCreated({...cardBeingCreated, pronunciation: e.target.value})}
                  placeholder="Ex: selam"
                  className="bg-surface w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none"
                />
              </div>

              {/* Champ Contexte */}
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-2">
                  💬 Contexte / Exemple (BACK de la carte)
                </label>
                <textarea
                  value={cardBeingCreated.context}
                  onChange={(e) => setCardBeingCreated({...cardBeingCreated, context: e.target.value})}
                  placeholder="Ex: Salutation courante utilisée à tout moment de la journée"
                  className="bg-surface w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-clay-500/20 focus:outline-none resize-none"
                  rows="3"
                />
              </div>

              {/* Sélecteur de Deck */}
              <div>
                <DeckSelector
                  value={cardBeingCreated.deck}
                  onChange={(v) => setCardBeingCreated({...cardBeingCreated, deck: v})}
                  decks={availableDecks}
                  label="📚 Deck *"
                  required
                />
              </div>

              {/* Aperçu de la carte */}
              <div className="bg-sand-100 rounded-xl p-4 border border-sand-200">
                <div className="label-caps mb-3">Aperçu de la carte</div>
                <div className="bg-surface rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-ink-700">FRONT:</span>
                    <span>{cardBeingCreated.name}{cardBeingCreated.frenchText && ` / ${cardBeingCreated.frenchText}`}</span>
                  </div>
                  <div className="border-t border-sand-200 my-2"></div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-ink-700">BACK:</span>
                    <div className="flex-1">
                      <div className="text-lg font-semibold text-ink-800" dir="rtl">{cardBeingCreated.translationTigrinya}</div>
                      {cardBeingCreated.pronunciation && (
                        <div className="text-ink-500 mt-1">🔊 {cardBeingCreated.pronunciation}</div>
                      )}
                      {cardBeingCreated.context && (
                        <div className="text-ink-500 mt-1 text-xs">💬 {cardBeingCreated.context}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-ink-500 mt-2">📚 Deck: {cardBeingCreated.deck}</div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCardModal(false);
                    setCardBeingCreated(null);
                  }}
                  className="flex-1 py-3 bg-sand-200 text-ink-700 rounded-lg font-medium hover:bg-sand-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleSaveToNotion(cardBeingCreated)}
                  disabled={!cardBeingCreated.name || !cardBeingCreated.translationTigrinya}
                  className="flex-1 py-3 bg-clay-500 text-onAccent rounded-lg font-medium hover:bg-clay-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span>💾</span>
                  <span>Enregistrer dans Notion</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;

